import type { MediaAsset } from "@lucky-try/contracts";
import { LinkSimpleIcon, TrashIcon, UploadSimpleIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { useMediaUpload } from "../components/media-picker";
import { Button } from "../components/ui/button";
import { ConfirmDialog, Dialog } from "../components/ui/dialog";
import { Field, Input, Select } from "../components/ui/form";
import {
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Pagination,
  Toolbar,
} from "../components/ui/misc";
import { api, errorMessage } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { mediaQuery } from "../lib/queries";
import { useCan } from "../lib/session";

const kilobytes = (bytes: number | null) =>
  bytes === null ? "—" : `${Math.round(bytes / 1024)} KB`;

/** Uploads keep their file name; external assets fall back to the last path segment. */
function assetLabel(asset: MediaAsset): string {
  if (asset.originalFilename) return asset.originalFilename;
  try {
    return new URL(asset.url).pathname.split("/").filter(Boolean).pop() ?? asset.storage;
  } catch {
    return asset.storage;
  }
}

export function MediaPage() {
  const search = useSearch({ from: "/authed/media" });
  const navigate = useNavigate({ from: "/media" });
  const queryClient = useQueryClient();
  const canWrite = useCan("media.write");
  const [term, setTerm] = useState(search.q ?? "");
  const [selected, setSelected] = useState<MediaAsset | null>(null);
  const [deleting, setDeleting] = useState<MediaAsset | null>(null);
  const [externalOpen, setExternalOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const media = useQuery(mediaQuery(search));
  const upload = useMediaUpload();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["media"] });

  const updateAlt = useMutation({
    mutationFn: (asset: MediaAsset) =>
      api<MediaAsset>(`/admin/media/${asset.id}`, { method: "PATCH", body: { alt: asset.alt } }),
    onSuccess: async () => {
      setSelected(null);
      await invalidate();
      toast.success("Alt text updated");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/admin/media/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      setDeleting(null);
      await invalidate();
      toast.success("Asset deleted");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (media.error) return <ErrorState error={media.error} onRetry={() => void media.refetch()} />;

  return (
    <>
      <PageHeader
        title="Media"
        description="Icons, previews and artwork stored in R2 or referenced externally."
        actions={
          canWrite && (
            <>
              <Button onClick={() => setExternalOpen(true)}>
                <LinkSimpleIcon /> Add external URL
              </Button>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) upload.mutate(file);
                }}
              />
              <Button
                variant="primary"
                disabled={upload.isPending}
                onClick={() => fileInput.current?.click()}
              >
                <UploadSimpleIcon /> {upload.isPending ? "Uploading…" : "Upload"}
              </Button>
            </>
          )
        }
      />

      <Toolbar>
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({
              search: (previous) => ({ ...previous, page: 1, q: term || undefined }),
            });
          }}
        >
          <Input
            placeholder="Search file name or alt text"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            className="w-64"
          />
          <Button type="submit">Search</Button>
        </form>
        <Select
          value={search.storage ?? ""}
          aria-label="Storage filter"
          onChange={(event) =>
            void navigate({
              search: (previous) => ({
                ...previous,
                page: 1,
                storage: (event.target.value || undefined) as typeof search.storage,
              }),
            })
          }
        >
          <option value="">All storage</option>
          <option value="r2">R2</option>
          <option value="external">External</option>
        </Select>
      </Toolbar>

      <div className="border-line bg-surface rounded-card overflow-hidden border">
        {media.isPending ? (
          <Loading />
        ) : media.data.items.length === 0 ? (
          <EmptyState>No assets match this filter.</EmptyState>
        ) : (
          <div className="grid grid-cols-3 gap-3 p-4 sm:grid-cols-5 xl:grid-cols-8">
            {media.data.items.map((asset) => (
              <button
                key={asset.id}
                type="button"
                onClick={() => setSelected(asset)}
                className="group ease-out-expo rounded-panel border-line hover:border-accent-line-hover overflow-hidden border text-left transition-colors duration-200"
              >
                <span className="cover block aspect-square rounded-none">
                  <img src={asset.url} alt={asset.alt ?? ""} loading="lazy" />
                </span>
                <span className="text-faint group-hover:text-body text-caption block truncate px-2 py-1.5 transition-colors">
                  {assetLabel(asset)}
                </span>
              </button>
            ))}
          </div>
        )}
        <Pagination
          page={media.data?.page ?? 1}
          pageSize={media.data?.pageSize ?? 60}
          total={media.data?.total ?? 0}
          onPage={(page) => void navigate({ search: (previous) => ({ ...previous, page }) })}
        />
      </div>

      <Dialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.originalFilename ?? "Asset"}
        width="lg"
        footer={
          canWrite && (
            <>
              <Button variant="danger" onClick={() => selected && setDeleting(selected)}>
                <TrashIcon /> Delete
              </Button>
              <Button
                variant="primary"
                disabled={updateAlt.isPending}
                onClick={() => selected && updateAlt.mutate(selected)}
              >
                Save alt text
              </Button>
            </>
          )
        }
      >
        {selected && (
          <div className="grid gap-4 sm:grid-cols-[200px_1fr]">
            <img
              src={selected.url}
              alt={selected.alt ?? ""}
              className="border-line w-full rounded border"
            />
            <div className="text-caption flex flex-col gap-3">
              <Field label="Alt text">
                <Input
                  value={selected.alt ?? ""}
                  onChange={(event) =>
                    setSelected({ ...selected, alt: event.target.value || null })
                  }
                />
              </Field>
              <dl className="text-muted grid grid-cols-[110px_1fr] gap-y-1">
                <dt>Storage</dt>
                <dd className="text-body">{selected.storage}</dd>
                <dt>Type</dt>
                <dd className="text-body">{selected.contentType ?? "—"}</dd>
                <dt>Dimensions</dt>
                <dd className="text-body">
                  {selected.width && selected.height ? `${selected.width}×${selected.height}` : "—"}
                </dd>
                <dt>Size</dt>
                <dd className="text-body">{kilobytes(selected.byteSize)}</dd>
                <dt>Added</dt>
                <dd className="text-body">{formatDateTime(selected.createdAt)}</dd>
              </dl>
              <a
                href={selected.url}
                target="_blank"
                rel="noreferrer"
                className="text-accent break-all hover:underline"
              >
                {selected.url}
              </a>
            </div>
          </div>
        )}
      </Dialog>

      {externalOpen && (
        <ExternalMediaDialog
          onClose={() => setExternalOpen(false)}
          onSaved={() => void invalidate()}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete asset"
        message="Assets referenced by a game or category cannot be deleted until the reference is removed."
        confirmLabel="Delete"
        danger
        pending={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        onClose={() => setDeleting(null)}
      />
    </>
  );
}

function ExternalMediaDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [url, setUrl] = useState("");
  const [alt, setAlt] = useState("");
  const save = useMutation({
    mutationFn: () => api<MediaAsset>("/admin/media/external", { body: { url, alt: alt || null } }),
    onSuccess: () => {
      toast.success("External asset registered");
      onSaved();
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Register external image"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Image URL" hint="HTTPS only; the file stays on the remote host.">
          <Input
            value={url}
            placeholder="https://"
            onChange={(event) => setUrl(event.target.value)}
          />
        </Field>
        <Field label="Alt text">
          <Input value={alt} onChange={(event) => setAlt(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
